import { ArrowsUpDownIcon, ChevronDownIcon, Cog6ToothIcon } from "@heroicons/react/24/outline";
import { useMemo, useState } from "react";
import { useDrop } from "react-dnd";
import { useTranslation } from "react-i18next";

import { IDismantlingBay, IDismantlingBaySetting } from "../../models/DismantlingBay";
import { DragTypes } from "../../models/DragTypes";
import {
  IPlannerDragItem,
  IPlannerVehicleRow,
  PlannerColourBy,
} from "../../models/ProductionPlanner";
import { VehicleComplexity } from "../../models/Vehicle";
import classNames from "../../utils/classNames";
import BayCapacityDots from "./BayCapacityDots";
import BayReorderModal from "./BayReorderModal";
import BaySettingsModal from "./BaySettingsModal";
import PlannerVehicleCard from "./PlannerVehicleCard";

type BayDropZoneVariant = "column" | "section";

interface BayDropZoneProps {
  bay: IDismantlingBay;
  date: string;
  vehicles: IPlannerVehicleRow[];
  colourBy: PlannerColourBy;
  onDrop: (item: IPlannerDragItem, bayId: string, date: string) => void;
  onReorder?: (bayId: string, date: string, vehicleIds: string[]) => void;
  variant?: BayDropZoneVariant;
  setting?: IDismantlingBaySetting;
  // Ids of vehicles matching the current search. Highlighting is display-only —
  // the cards stay in their dismantling order so the up/down controls and the
  // sequence an operator works to don't shift under a search.
  searchMatchIds?: Set<string>;
  isSearchActive?: boolean;
}

const variantStyles: Record<
  BayDropZoneVariant,
  {
    wrapper: string;
    header: string;
    title: string;
    icon: string;
    dropArea: string;
    emptyPlaceholder: string;
  }
> = {
  column: {
    wrapper: "bg-gray-50 border border-gray-200 rounded-md flex flex-col min-w-[220px]",
    header: "px-3 py-2 border-b border-gray-200 bg-white rounded-t-md",
    title: "text-xs font-semibold uppercase tracking-wide text-gray-800 truncate",
    icon: "h-4 w-4 text-gray-400",
    dropArea: "flex-1 p-2 space-y-2 min-h-[160px] transition-colors",
    emptyPlaceholder:
      "h-32 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center text-xs text-gray-400",
  },
  section: {
    wrapper: "border-t border-gray-200 first:border-t-0",
    header: "px-3 py-2 bg-white border-b-2 border-primary-400",
    title: "text-[11px] font-bold uppercase tracking-wide text-gray-900 truncate",
    icon: "h-3.5 w-3.5 text-gray-400",
    dropArea: "p-2 space-y-2 min-h-[120px] transition-colors",
    emptyPlaceholder:
      "h-20 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center text-xs text-gray-400",
  },
};

export default function BayDropZone({
  bay,
  date,
  vehicles,
  colourBy,
  onDrop,
  onReorder,
  variant = "column",
  setting,
  searchMatchIds,
  isSearchActive = false,
}: BayDropZoneProps) {
  const { t } = useTranslation("production");
  const styles = variantStyles[variant];
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const [reorderOpen, setReorderOpen] = useState<boolean>(false);
  // Collapsing is per line per day - the component instance is already keyed on
  // both, so plain state is all it takes. Deliberately not persisted.
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

  const orderedVehicles = useMemo(
    () => [...vehicles].sort((a, b) => a.dismantlingBayOrder - b.dismantlingBayOrder),
    [vehicles]
  );

  const moveVehicle = (index: number, direction: -1 | 1) => {
    if (!onReorder) return;
    const target = index + direction;
    if (target < 0 || target >= orderedVehicles.length) return;
    const ids = orderedVehicles.map((v) => v.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    onReorder(bay.id, date, ids);
  };

  const capacity = setting?.capacity ?? bay.capacity;
  const easyCap = setting?.easy ?? bay.easy;
  const mediumCap = setting?.medium ?? bay.medium;
  const hardCap = setting?.hard ?? bay.hard;

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: DragTypes.PLANNER_VEHICLE,
      canDrop: (item: IPlannerDragItem) =>
        !(item.currentBayId === bay.id && item.currentPlannedOn?.startsWith(date)),
      drop: (item: IPlannerDragItem) => onDrop(item, bay.id, date),
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [bay.id, date, onDrop]
  );

  const isCollapsible = variant === "section";
  const hasSearchMatch = orderedVehicles.some((v) => searchMatchIds?.has(v.id) ?? false);
  // Stay open while a card is dragged over, and while the line holds a search
  // hit - otherwise the planner would scroll to something that isn't shown.
  const collapsed = isCollapsible && isCollapsed && !hasSearchMatch && !(isOver && canDrop);

  const { easy, medium, hard } = useMemo(() => {
    const e = vehicles.filter((v) => v.complexity === VehicleComplexity.Easy).length;
    const m = vehicles.filter((v) => v.complexity === VehicleComplexity.Medium).length;
    const h = vehicles.filter((v) => v.complexity === VehicleComplexity.Hard).length;
    return { easy: e, medium: m, hard: h };
  }, [vehicles]);

  return (
    <div
      ref={drop}
      className={classNames(
        styles.wrapper,
        isOver && canDrop ? "ring-2 ring-primary-400" : ""
      )}
    >
      <div className={styles.header}>
        <div className="flex items-center justify-between gap-x-2">
          {isCollapsible ? (
            <button
              type="button"
              onClick={() => setIsCollapsed((prev) => !prev)}
              className="flex items-center gap-x-1 min-w-0 flex-1 text-left"
              title={collapsed ? t("Expand") : t("Collapse")}
            >
              <ChevronDownIcon
                className={classNames(
                  styles.icon,
                  "shrink-0 text-gray-500 transition-transform",
                  collapsed ? "-rotate-90" : ""
                )}
              />
              <span className={styles.title}>{bay.name}</span>
            </button>
          ) : (
            <span className={styles.title}>{bay.name}</span>
          )}
          <div className="flex items-center gap-x-1.5 shrink-0">
            {onReorder && (
              <button
                type="button"
                onClick={() => setReorderOpen(true)}
                disabled={orderedVehicles.length < 2}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-40 disabled:hover:text-gray-400"
                title={t("Re-order")}
              >
                <ArrowsUpDownIcon className={styles.icon} />
              </button>
            )}
            <button
              type="button"
              onClick={() => setSettingsOpen(true)}
              className="text-gray-400 hover:text-gray-600"
              title={t("Edit capacity")}
            >
              <Cog6ToothIcon className={styles.icon} />
            </button>
          </div>
        </div>
        <div className="text-[11px] text-gray-500 mt-0.5">
          {t("{{count}} / {{capacity}} vehicles", { count: vehicles.length, capacity })}
        </div>
        <div className="mt-1">
          <BayCapacityDots
            easyFilled={easy}
            easyCap={easyCap}
            mediumFilled={medium}
            mediumCap={mediumCap}
            hardFilled={hard}
            hardCap={hardCap}
          />
        </div>
      </div>

      <div
        className={classNames(
          styles.dropArea,
          isOver && canDrop ? "bg-primary-50" : "",
          collapsed ? "hidden" : ""
        )}
      >
        {orderedVehicles.length === 0 ? (
          <div className={styles.emptyPlaceholder}>{t("Drag here")}</div>
        ) : (
          orderedVehicles.map((v, index) => (
            <PlannerVehicleCard
              key={v.id}
              vehicle={v}
              colourBy={colourBy}
              onMoveUp={onReorder ? () => moveVehicle(index, -1) : undefined}
              onMoveDown={onReorder ? () => moveVehicle(index, 1) : undefined}
              canMoveUp={index > 0}
              canMoveDown={index < orderedVehicles.length - 1}
              isSearchMatch={searchMatchIds?.has(v.id) ?? false}
              isSearchDimmed={isSearchActive && !(searchMatchIds?.has(v.id) ?? false)}
            />
          ))
        )}
      </div>

      <BaySettingsModal
        open={settingsOpen}
        setOpen={setSettingsOpen}
        bay={bay}
        date={date}
      />

      {onReorder && (
        <BayReorderModal
          open={reorderOpen}
          setOpen={setReorderOpen}
          bay={bay}
          date={date}
          vehicles={orderedVehicles}
          onReorder={onReorder}
        />
      )}
    </div>
  );
}
